import isMergedCell from 'notebook/structured-tables/util/isMergedCell';
import type {Cell} from 'notebook/tables/constants/CellConstants';
import {type Table, type TableData} from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {computeIsRealIndex} from 'notebook/tables/store/logic/TableSelectionLogic';
import {
  getLockedCellWarningMessage,
  LockedCellWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';

export type PasteRejectionSeverity = 'error' | 'warn';

export interface PasteRejectionReason {
  reason: string;
  severity: PasteRejectionSeverity;
}

export interface PasteValidationOptions {
  supportsMerge: boolean;
  preserveMergedCellsInPaste: boolean;
  allowMergeSelection?: (start: TableIndex, end: TableIndex) => boolean;
  isAnyCellLockedOrArchived: (
    cells: Array<{cellId: {row: number; column: number}; evaluatedCell: EvaluatedCell}>
  ) => boolean;
}

/**
 * Collects the evaluated cells in the paste destination region.
 */
export function computePasteDestinationCells(
  table: TableData,
  computedTable: Array<Array<EvaluatedCell>>,
  cells: Array<Array<Cell>>,
  minI: number,
  minJ: number
): Array<{cellId: {row: number; column: number}; evaluatedCell: EvaluatedCell}> {
  const pasteRowCount = cells.length;
  const pasteColCount = cells[0].length;
  const pasteFullColCount = Math.min(pasteColCount + minJ, table.table[0].length);
  const pasteFullRowCount = Math.min(pasteRowCount + minI, table.table.length);

  const pasteDestinationCells: Array<{
    cellId: {row: number; column: number};
    evaluatedCell: EvaluatedCell;
  }> = [];

  for (let tableRow = minI; tableRow < pasteFullRowCount; tableRow++) {
    for (let tableColumn = minJ; tableColumn < pasteFullColCount; tableColumn++) {
      pasteDestinationCells.push({
        cellId: {row: tableRow, column: tableColumn},
        evaluatedCell: computedTable[tableRow][tableColumn],
      });
    }
  }
  return pasteDestinationCells;
}

/**
 * Determines whether a paste operation should be rejected. Returns null if the paste is
 * allowed, or a `PasteRejectionReason` with a user-facing message and severity if not.
 *
 * This replaces the side-effectful `_isPasteAllowed()` from TableEditable. The caller is
 * responsible for surfacing the reason via notifications.
 */
export function computePasteRejectionReason(
  table: TableData,
  computedTable: Array<Array<EvaluatedCell>>,
  cells: Array<Array<Cell>>,
  minI: number,
  minJ: number,
  options: PasteValidationOptions
): PasteRejectionReason | null {
  const isPastingMergedCell = cells.some((row) => row.some((cell) => isMergedCell(cell)));

  if (isPastingMergedCell && !options.preserveMergedCellsInPaste && !options.supportsMerge) {
    return {reason: 'Merged cells are not supported for this table.', severity: 'error'};
  }

  // Early return if we are outside of the table (ghost cells are not actual cells)
  if (!computeIsRealIndex([minI, minJ], table as Table)) {
    return null;
  }

  // Check if any cells in the paste destination region are locked or archived
  const pasteDestinationCells = computePasteDestinationCells(table, computedTable, cells, minI, minJ);
  if (options.isAnyCellLockedOrArchived(pasteDestinationCells)) {
    return {reason: getLockedCellWarningMessage(LockedCellWarningType.GENERIC), severity: 'warn'};
  }

  if (isPastingMergedCell && options.allowMergeSelection) {
    const invalidMerge = cells.some((row, rowIdx) =>
      row.some((cell, colIdx) => {
        if (isMergedCell(cell)) {
          const startI = minI + rowIdx;
          const startJ = minJ + colIdx;
          const endI = startI + (cell.mergeRowSpan ?? 1) - 1;
          const endJ = startJ + (cell.mergeColSpan ?? 1) - 1;

          // If the range starts beyond the table width, structured tables will drop/ignore it
          if (startJ >= table.table[0].length) {
            return false;
          }

          return !options.allowMergeSelection!([startI, startJ], [endI, endJ]);
        }
        return false;
      })
    );

    if (invalidMerge) {
      return {
        reason: 'The merged range(s) in the content to be pasted are invalid for this table.',
        severity: 'error',
      };
    }
  }

  // Keep track of all cells involved in a merged cell, since only the top left cell has any properties
  const mergedCellsInPasteByIndex: {[key: string]: boolean} = {};

  const rowCount = cells.length;
  const colCount = cells[0].length;
  const fullColCount = Math.min(colCount + minJ, table.table[0].length);
  const fullRowCount = Math.min(rowCount + minI, table.table.length);
  for (let tableRow = minI; tableRow < fullRowCount; tableRow++) {
    const pasteRow = tableRow - minI;
    for (let tableColumn = minJ; tableColumn < fullColCount; tableColumn++) {
      const pasteColumn = tableColumn - minJ;
      const pasteCell = cells[pasteRow][pasteColumn];
      const evaluatedCell = computedTable[tableRow][tableColumn];
      const tableCell = table.table[tableRow][tableColumn];
      const isPasteMergedCell = mergedCellsInPasteByIndex[`${pasteRow},${pasteColumn}`];

      const maxI = minI + rowCount - 1;
      const maxJ = minJ + colCount - 1;
      if (
        (rowCount > 1 || colCount > 1) &&
        evaluatedCell.mergedBox &&
        (evaluatedCell.mergedBox.minI < minI ||
          evaluatedCell.mergedBox.maxI > maxI ||
          evaluatedCell.mergedBox.minJ < minJ ||
          evaluatedCell.mergedBox.maxJ > maxJ)
      ) {
        // The destination merged cell must be fully contained within the paste
        return {
          reason: 'Cannot paste cells that partially intersect a merged cell.',
          severity: 'error',
        };
      }
      if (options.preserveMergedCellsInPaste) {
        const mergedCellErrorMessage =
          'Cannot paste cells where the structure of merged and unmerged cells is misaligned.';
        // If the cell is merged
        if (isMergedCell(pasteCell)) {
          // Mark all the cells in the merged range as merged
          for (let i = 0; i < (pasteCell.mergeRowSpan || 1); i++) {
            for (let j = 0; j < (pasteCell.mergeColSpan || 1); j++) {
              mergedCellsInPasteByIndex[`${pasteRow + i},${pasteColumn + j}`] = true;
            }
          }

          // Only paste a merged cell if it matches the destination's size
          if (
            tableCell.mergeColSpan !== pasteCell.mergeColSpan ||
            tableCell.mergeRowSpan !== pasteCell.mergeRowSpan
          ) {
            return {reason: mergedCellErrorMessage, severity: 'error'};
          }
        } else if (evaluatedCell.mergedBox && !isPasteMergedCell) {
          // If the destination is a merged cell
          const isPastingIntoHiddenMergedCell =
            evaluatedCell.mergedBox.minI !== tableRow || evaluatedCell.mergedBox.minJ !== tableColumn;

          if (isPastingIntoHiddenMergedCell) {
            // Don't paste a single cell that collides with a hidden merged cell
            return {reason: mergedCellErrorMessage, severity: 'error'};
          }
        }
      }
    }
  }
  return null;
}
